import type { PayloadAction } from '@reduxjs/toolkit';
import type { EditorState } from './state';
import type { QuizLoaderSettings } from '@/components/quiz-runtime/quizLoader';
import { withQuizLoader } from './appearanceEdits';

/**
 * Вид загрузчика своими полями (слово владельца 21.10.2026): тело действия
 * `setQuizLoader`. Правка одного поля не трогает соседние; `undefined`
 * снимает поле — «как в стиле»; снято последнее или пришёл `null` — поля
 * `quizLoader` в настройках нет вовсе (отсутствие = загрузчик пака).
 *
 * Живёт здесь, а не в editorSlice.ts: фасад держит храповик длины (WEB-2552).
 * Создатель действия и регистрация — `./screenFieldActions.ts`, имя
 * `editor/setQuizLoader`; фасад его не реэкспортирует (храповик красный на
 * основе ветки), панель берёт создатель из `./screenFieldActions`.
 */
export type QuizLoaderPatch = {
  [K in keyof QuizLoaderSettings]?: QuizLoaderSettings[K] | undefined;
};

/**
 * Слияние правки с прежним видом: одна функция на поле воронки
 * (`settings.quizLoader`) и на поле блока (`block.loaderLook`, задание
 * stylepack/loader-block-look). `undefined` снимает поле; `null` или снято
 * последнее — `undefined` (поля нет вовсе).
 */
export function mergeQuizLoaderPatch(
  prev: QuizLoaderSettings | undefined,
  patch: QuizLoaderPatch | null,
): QuizLoaderSettings | undefined {
  const next: Record<string, unknown> = patch ? { ...prev, ...patch } : {};
  for (const key of Object.keys(next)) {
    if (next[key] === undefined) delete next[key];
  }
  return Object.keys(next).length > 0
    ? (next as QuizLoaderSettings)
    : undefined;
}

export function setQuizLoaderReducer(
  state: EditorState,
  action: PayloadAction<QuizLoaderPatch | null>,
): EditorState {
  // WEB-3767: правило правки — `withQuizLoader` (`./appearanceEdits.ts`).
  return {
    ...state,
    settings: withQuizLoader(state.settings, action.payload),
    isDirty: true,
  };
}
